<script lang="ts">
  import { Chip, type FacetValue } from "@otto-ec/heureka-components/facet-value";
  import { type FacetValueLabelOptions, Text } from "@otto-ec/heureka-components/facet-value-label";
  import DeselectAll from "../facetvaluetags/DeselectAll.svelte";
  import type { DeselectAllConfiguration } from "./type";
  import { OverflowBox } from "@otto-ec/heureka-components/facet-value-group";
  import { dereference, readLabel, updateLabel, updateStatus } from "../tracking/FeatureTracking";
  import { setChangeLabels } from "../filter/FilterTracking";
  import { getElementById } from "../util/Utils";
  import { emptyCategoryPathStore, selectedCategoryPathValues } from "../categorypath/categoryPathStore";
  import { clearFormInput, interceptSubmit } from "../filter/FilterFormActions";
  import { heureka } from "@otto-ec/heureka";
  import { events } from "../events";
  import FilterSwitchBanner from "../filterBanner/FilterSwitchBanner";

  const defaultFacetValueLabelOptions: Partial<FacetValueLabelOptions> = {
    size: "75",
    showCount: false,
  };

  interface Props {
    deselectAll: DeselectAllConfiguration;
    values?: FacetValue[];
    hideDeselectAllOnDesktopView: boolean;
    rule?: string;
  }

  const { deselectAll, values = [], hideDeselectAllOnDesktopView, rule }: Props = $props();
  const titles = values.map((value) => value.title);
  const initialSelectedIds = values.map((value) => value.referencedFacetValueId);
  const selectedCategories: FacetValue[] = $derived(
    Array.from($selectedCategoryPathValues)
      .filter((value) => !initialSelectedIds.includes(value.id))
      .map((value) => {
        return {
          id: value.id,
          checked: value.checked,
          title: value.title,
          referencedFacetValueId: value.ref || value.id,
        };
      }),
  );
  const allValues = $derived(values.concat(selectedCategories));
  const hasValues = $derived(allValues?.length > 0);
  const filterSwitchBanner = FilterSwitchBanner.fromTemplate();

  function onchange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      /*                                                                                           */
      /*                                                   */
      if (filterForm) {
        setChangeLabels(filterForm, { san_FacetLocation: "list_initial" });
        if (event.target instanceof HTMLInputElement && event.target.checked === false) {
          if (event.target.value === filterSwitchBanner?.bannerSwitch?.value) {
            filterSwitchBanner?.deleted("x_single_top");
          }
          filterForm.dataset.tsFeatureFilterMethod = "x_single_top";
        } else {
          filterForm.dataset.tsFeatureFilterMethod = readLabel(referenced, "san_FilterMethod");
        }
      }
    }

    heureka.filterSection_change.emit({ target: event.target });
  }

  function onClickDeselectAll() {
    filterSwitchBanner?.deleted("x_all_top");
    allValues.forEach((value) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById(value.referencedFacetValueId);
        if (referenced) {
          updateLabel(referenced, "san_FilterMethod", "x_all_top");
          updateLabel(referenced, "san_FacetLocation", "list_initial");
          updateStatus(referenced, "deleted", 2);
        }
      }
    });
  }

  function onSubmitDeselectAll(event: SubmitEvent) {
    emptyCategoryPathStore();
    allValues.forEach((value) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById<HTMLInputElement>(value.referencedFacetValueId);
        if (referenced) {
          clearFormInput(referenced);
        }
      }
      interceptSubmit(event);
    });
  }

  $effect(() => {
    allValues.forEach((value, index) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById(value.referencedFacetValueId);
        if (referenced) {
          updateLabel(referenced, "san_FacetValueTagPosition", (index + 1).toString());
        }
      }
    });
  });

  $effect(() => {
    if (values.length > 0) {
      events.facetValueTagsLoaded(rule, titles);
    }
  });
</script>

{#snippet closeIcon()}
  <oc-icon-v1 class="hc_facetValue__icon" type="close" size="50"></oc-icon-v1>
{/snippet}

{#snippet chip(facetValue: FacetValue)}
  <Chip class={[{ js_clearInput: facetValue.checked }]} variantGray {...facetValue}>
    <Text {...facetValue} {...defaultFacetValueLabelOptions} />
    {#if facetValue.checked}
      {@render closeIcon()}
    {/if}
  </Chip>
{/snippet}

<div class="hc_facetValueTags" {onchange}>
  {#if hasValues}
    <div class="hc_facetValueTags__content">
      <div class="hc_facetValueTags__values">
        <OverflowBox values={allValues} item={chip}></OverflowBox>
      </div>
      <div
        class="hc_facetValueTags__deselectAll"
        class:hc_facetValueTags__deselectAll--hide-desktop={hideDeselectAllOnDesktopView}
      >
        <DeselectAll onclick={onClickDeselectAll} onsubmit={onSubmitDeselectAll} {...deselectAll} />
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .hc_facetValueTags {
    &__content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px 0;
    }

    &__values {
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;

      .hc_facetValue__icon {
        width: tokens.$oc-semantic-spacing-75;
        height: tokens.$oc-semantic-spacing-75;
        margin-left: tokens.$oc-semantic-spacing-25;
      }
    }

    &__deselectAll {
      white-space: nowrap;
      text-align: right;
      padding-left: tokens.$oc-semantic-spacing-75;
      padding-right: tokens.$oc-semantic-spacing-25;
    }
  }
</style>
